import { useEffect } from 'react';

/*
  Hold the page still while a modal is open.

  Every dialog used to do its own `document.body.style.overflow`, which
  went wrong in three ways:

    * Some never locked at all — the planet cards, the image zoom and
      both admin dialogs — so the page scrolled away behind them.
    * Stacked dialogs fought each other. Opening a product, then its
      image lightbox, then closing only the lightbox set overflow back
      to '' and freed the page while the product modal was still up.
      Hence the reference count below.
    * `overflow: hidden` on <body> does nothing on iOS Safari, which
      scrolls the viewport regardless. Only taking the body out of
      flow with `position: fixed` actually stops it — and that loses
      the scroll position unless it is put back by hand.
*/

const state = {
  locks: 0,
  scrollY: 0,
  previous: null,
};

const PROPS = [
  'position',
  'top',
  'left',
  'right',
  'width',
  'overflow',
  'paddingRight',
];

const lock = () => {
  state.locks += 1;
  if (state.locks > 1) return; // already held by an outer dialog

  const { body, documentElement } = document;

  state.scrollY = window.scrollY || documentElement.scrollTop || 0;
  state.previous = Object.fromEntries(PROPS.map((p) => [p, body.style[p]]));

  /* The scrollbar is about to disappear. Without this the whole page
     jumps sideways by its width the instant a modal opens. */
  const gap = window.innerWidth - documentElement.clientWidth;

  body.style.position = 'fixed';
  body.style.top = `-${state.scrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  if (gap > 0) body.style.paddingRight = `${gap}px`;
};

const unlock = () => {
  state.locks = Math.max(0, state.locks - 1);
  if (state.locks > 0 || !state.previous) return; // a dialog is still open

  const { body } = document;
  PROPS.forEach((p) => {
    body.style[p] = state.previous[p] || '';
  });
  state.previous = null;

  /* Back where they were. `behavior: 'instant'` matters — the site sets
     `scroll-behavior: smooth` globally, so the default would glide the
     page back up and look like a bug. */
  window.scrollTo({ top: state.scrollY, left: 0, behavior: 'instant' });
};

/**
 * @param active  true while the dialog is on screen.
 *
 * Safe to call unconditionally at the top of a component, which is
 * what lets it sit above an early `if (!open) return null`.
 */
export default function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;

    lock();
    return unlock;
  }, [active]);
}
